import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  Checkbox,
  Divider,
  DrawerButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stat,
  StatLabel,
  Text,
  VStack,
} from '@frontend/legacy-components';

import { useAppSelector } from 'store/hooks';

import { DRAWER_MODES } from '../constants';
import { setPointOfContact } from '../data/actions';
import {
  getConfirmationEmail,
  getContact,
  getDrawerMode,
  getPointOfContact,
  getPointOfContactErrors,
  getWithConfirmation,
} from '../data/reducer';

type ReviewOrderFooterProps = {
  isCompletingOrder: boolean;
  handleSetDrawerTab: (tab: string) => void;
  onCompleteOrder: () => void;
};

export const ReviewOrderFooter = ({
  isCompletingOrder,
  handleSetDrawerTab,
  onCompleteOrder,
}: ReviewOrderFooterProps) => {
  const dispatch = useDispatch();
  const withConfirmation = useAppSelector(getWithConfirmation);
  const pointOfContact = useAppSelector(getPointOfContact);
  const confirmationEmail = useAppSelector(getConfirmationEmail);
  const contact = useAppSelector(getContact);
  const errors = useAppSelector(getPointOfContactErrors);
  const isValid = Object.keys(errors).length === 0;
  const isEditingOrder = useAppSelector(getDrawerMode) === DRAWER_MODES.EDIT;

  const setSendConfirmation = useCallback(
    (sendConfirmation: boolean) => {
      const payload = sendConfirmation
        ? {
            withConfirmation: true,
            pointOfContact,
            confirmationEmail,
          }
        : {
            withConfirmation: false,
            pointOfContact: undefined,
            confirmationEmail: undefined,
          };
      dispatch(setPointOfContact(payload));
    },
    [confirmationEmail, pointOfContact, dispatch],
  );

  const setPointOfContactName = useCallback(
    (name: string) => {
      dispatch(
        setPointOfContact({
          withConfirmation,
          pointOfContact: name.trim() || undefined,
          confirmationEmail,
        }),
      );
    },
    [confirmationEmail, withConfirmation, dispatch],
  );

  const setPointOfContactEmail = useCallback(
    (email: string) => {
      dispatch(
        setPointOfContact({
          withConfirmation,
          pointOfContact,
          confirmationEmail: email.trim() || undefined,
        }),
      );
    },
    [pointOfContact, withConfirmation, dispatch],
  );

  return (
    <VStack w="100%" align="stretch">
      <Checkbox
        isChecked={withConfirmation}
        onChange={(event) => setSendConfirmation(event.target.checked)}
      >
        {isEditingOrder
          ? 'Notify customer of update to order'
          : 'Send order confirmation email'}
      </Checkbox>
      {withConfirmation && (
        <>
          {contact && (
            <Stat>
              <StatLabel>Primary Recipient</StatLabel>
              <Text>
                <Text as="span" fontWeight="heavy">
                  {contact.firstName} {contact.lastName}
                </Text>{' '}
                {contact.email}
              </Text>
            </Stat>
          )}
          <Divider />
          <HStack align="start">
            <FormControl>
              <FormLabel>Additional Recipient Name</FormLabel>
              <Input
                key={pointOfContact}
                defaultValue={pointOfContact}
                onBlur={(event) => setPointOfContactName(event.target.value)}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.confirmationEmail}>
              <FormLabel>Email</FormLabel>
              <Input
                key={confirmationEmail}
                defaultValue={confirmationEmail}
                onBlur={(event) => setPointOfContactEmail(event.target.value)}
              />
              <FormErrorMessage>{errors.confirmationEmail}</FormErrorMessage>
            </FormControl>
          </HStack>
        </>
      )}
      <DrawerButtonGroup>
        <Button
          variant="secondary"
          onClick={() => handleSetDrawerTab('customer_information')}
          isDisabled={isCompletingOrder}
        >
          Go Back
        </Button>
        <Button
          onClick={onCompleteOrder}
          isLoading={isCompletingOrder}
          isDisabled={!isValid}
        >
          Complete Order
        </Button>
      </DrawerButtonGroup>
    </VStack>
  );
};
